import React, { useState } from "react";
import axios from "axios";
import SmoothCollapse from "./SmoothCollapse";
import { destroyAllTooltips } from "./utils/tooltipUtils";

const CertRowDetails = ({ cert, isExpanded, onRefresh, showToast, handleResolve, country }) => {
  const [loading, setLoading] = useState(false);
  const [isRefOpen, setIsRefOpen] = useState(false);
  const [isPocOpen, setIsPocOpen] = useState(false);
  const [isAffectedOpen, setIsAffectedOpen] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(cert.is_bookmarked || false);

  const {
    id,
    vendor,
    affected_products,
    affected_versions = [],
    cve,
    description,
    published_date,
    last_modified_date,
    vuln_status,
    country_code,
    guid,
    created_at,
    cvss_version,
    score_cvss,
    severity,
    attack_vector,
    attack_complexity,
    privileges_required,
    user_interaction,
    scope,
    confidentiality_impact,
    integrity_impact,
    availability_impact,
    exploitability_score,
    impact_score,
    affected_country = [],
    cve_refs = [],
    poc_refs = [],
  } = cert;

  const splitList = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    try {
      return JSON.parse(input);
    } catch {
      return input.toString().split(",").map((item) => item.trim());
    }
  };
  
  const handleShare = () => {
	  const subject = encodeURIComponent(
		`Vulnerability ${cert.cve || cert.id}`
	  );

	  const body = encodeURIComponent(
		`Hi,\n\nPlease find below the vulnerability details.\n\n` +
		`CVE: ${cert.cve || "—"}\n` +
		`Vendor: ${cert.vendor || "—"}\n` +
		`Severity: ${cert.severity || "—"}\n` +
		`CVSS: ${cert.score_cvss || "—"} (${cert.cvss_version || "—"})\n` +
		`Status: ${cert.vuln_status || "—"}\n` +
		`Published: ${cert.published_date || "—"}\n` +
		`Last modified: ${cert.last_modified_date || "—"}\n` +
		`Affected countries: ${
		  Array.isArray(cert.affected_country) && cert.affected_country.length
			? cert.affected_country.map(c => c.toUpperCase()).join(", ")
			: "—"
		}\n\n` +
		`Description:\n${cert.description || "No description provided."}\n\n` +
		(Array.isArray(cert.affected_products) && cert.affected_products.length
		  ? `Affected products:\n${cert.affected_products.join(", ")}\n\n`
		  : "") +
		`Regards,\nSecurity Team`
	  );

	  window.location.href = `mailto:?subject=${subject}&body=${body}`;
	};

  const getFlagEmoji = (cc) =>
    cc.toLowerCase() === "unknown"
      ? "🌐"
      : String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
  
  const handleRefreshCVE = async () => {
	  if (!cve) return;

	  destroyAllTooltips();
	  setLoading(true);
	  try {
		const res = await axios.post("/api/refresh_cve.php", { cve });
		if (res.data?.status === "success") {
		  showToast(`${cve} refreshed!`, "success");
		  if (typeof onRefresh === "function") onRefresh();
		} else {
		  showToast(`${res.data?.message || "Unknown error"}`, "danger");
		}
	  } catch (err) {
		showToast("Failed to refresh CVE.", "danger");
	  } finally {
		setLoading(false);
	  }
	};
  
  const handleSendToCockpit = async () => {
	  const payload = {
		vulnerability_id: cert.id,
		type: "alert",
		generated_by: cert.generated_by || "anonymous", // idéalement tu passes le currentUser prop
		description: cert.description,
		published_date: cert.published_date,
		last_modified_date: cert.last_modified_date,
		cve: cert.cve,
		vulnerabilites: cert.vulnerabilites,
		vuln_status: cert.vuln_status,
		cvss: cert.score_cvss,
		alerte: cert.alerte,
		faille_exploitee: cert.faille_exploitee,
		correctif: cert.correctif,
		contournement: cert.contournement,
		cvss_version: cert.cvss_version,
		attack_vector: cert.attack_vector,
		attack_complexity: cert.attack_complexity,
		privileges_required: cert.privileges_required,
		user_interaction: cert.user_interaction,
		scope: cert.scope,
		confidentiality_impact: cert.confidentiality_impact,
		integrity_impact: cert.integrity_impact,
		availability_impact: cert.availability_impact,
		severity: cert.severity,
		exploitability_score: cert.exploitability_score,
		impact_score: cert.impact_score,
		cti_team: "CTI Team",
		references: cert.cve_refs,
		poc_references: cert.poc_refs,
	  };

	  try {
		const res = await axios.post("/api/send_to_cockpit.php", payload);
		if (res.data.success) {
		  showToast("Cockpit ticket created!", "success");
		  onRefresh();
		} else {
		  showToast("Cockpit error: " + res.data.message, "danger");
		}
	  } catch (err) {
		showToast("Network error sending to Cockpit", "danger");
		console.error(err);
	  }
	};
	
	const handleBookmarkCountry = async (vulnId) => {
	  destroyAllTooltips();
	  setLoading(true);
	  try {
		const res = await axios.post("/api/bookmark_alert.php", {
		  vulnerability_id: vulnId,
		  location: country.toLowerCase()
		});

		if (res.data?.success) {
		  setIsBookmarked(true);
		  showToast("Alert acknowleged for " + country.toUpperCase(), "success");
		  if (typeof onRefresh === "function") onRefresh();
		} else {
		  showToast(res.data?.message || "Bookmark failed", "danger");
		}
	  } catch (err) {
		console.error("Bookmark error", err);
		showToast("Bookmark failed (network error)", "danger");
	  } finally {
		setLoading(false);
	  }
	};

  return (
    <tr className="detail-row">
      <td colSpan="7" className="p-0">
        <SmoothCollapse show={isExpanded}>
          <div className="cert-details-container p-4 position-relative">
			{cve && (
			  <div style={{ position: "absolute", top: 10, right: 15 }}>
				<div
				  style={{
					display: "flex",
					gap: "8px",
					alignItems: "center",
					minHeight: "1.4rem",
				  }}
				>
				  {/* ✉️ Send button (France vs other) */}
				  {country.toLowerCase() === "fr" ? (
					!guid && (
					  <button
						className="btn btn-sm btn-link text-light"
						data-bs-toggle="tooltip"
						data-bs-placement="top"
						title="Send to Cockpit"
						onClick={handleSendToCockpit}
						style={{ fontSize: "1.4rem", color: "#f8f9fa" }}
					  >
						<i className="bi bi-send-plus" />
					  </button>
					)
				  ) : (
					!isBookmarked && (
					  <button
						className="btn btn-sm btn-link text-light"
						data-bs-toggle="tooltip"
						data-bs-placement="top"
						title="Acknowledge this alert"
						onClick={() => handleBookmarkCountry(cert.id)}
						style={{ fontSize: "1.4rem", color: "#f8f9fa" }}
					  >
						<i className="bi bi-send-plus" />
					  </button>
					)
				  )}

				  {/* 🔗 Share */}
				  <button
					className="btn btn-sm btn-link text-light"
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					title="Share"
					onClick={handleShare}
					style={{ fontSize: "1.4rem", color: "#f8f9fa" }}
				  >
					<i className="bi bi-share" />
				  </button>

				  {/* 🚀 Rocket / Spinner */}
				  <div
					style={{
					  width: "1.4rem",
					  height: "1.4rem",
					  display: "flex",
					  alignItems: "center",
					  justifyContent: "center",
					}}
				  >
					{loading ? (
					  <div
						className="spinner-border spinner-border-sm"
						style={{ color: "#af7544", width: "1.1rem", height: "1.1rem" }}
						role="status"
					  >
						<span className="visually-hidden">Loading...</span>
					  </div>
					) : (
					  <button
						className="btn btn-sm btn-link text-light p-0 m-0"
						data-bs-toggle="tooltip"
						data-bs-placement="top"
						title="Refresh CVE Data"
						onClick={handleRefreshCVE}
						style={{
						  fontSize: "1.4rem",
						  color: "#f8f9fa",
						  lineHeight: "1",
						  height: "1.4rem",
						}}
					  >
						<i className="bi bi-rocket-takeoff" />
					  </button>
					)}
				  </div>
				</div>
			  </div>
			)}
            {/* 🧾 Summary */}
			<div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
			  {/* 📄 Text section */}
			  <div className="flex-grow-1">
				<p className="mb-1 text-light">
				  <strong>Vendor:</strong>{" "}
				  <span className="badge bg-dark border border-secondary">{vendor || "—"}</span>
				</p>

				<p className="mb-1 text-light">
				  <strong>Affected Products:</strong>{" "}
				  {splitList(affected_products).map((prod, i) => (
					<span key={i} className="badge bg-dark border border-secondary me-1">
					  {prod}
					</span>
				  ))}
				</p>

				{cve && (
				  <p className="mb-1 text-light">
					<strong>CVE ID:</strong>{" "}
					<span className="text-warning">{cve}</span>
				  </p>
				)}

				<p className="mb-1 text-light">
				  <strong>CVE Published:</strong>{" "}
				  <span className="text-secondary">{published_date || "—"}</span>
				</p>

				<p className="mb-1 text-light">
				  <strong>Last Modified:</strong>{" "}
				  <span className="text-secondary">{last_modified_date || "—"}</span>
				</p>

				<p className="mb-1 text-light">
				  <strong>Vulnerability Status:</strong>{" "}
				  <span className="fw-bold text-primary">{vuln_status || "—"}</span>
				</p>

				{/* 🌍 Affected countries */}
				{Array.isArray(affected_country) && affected_country.length > 0 && (
				  <div className="d-flex align-items-center flex-wrap mt-2">
					<strong className="text-light me-2">Affected Countries:</strong>
					{affected_country.map((cc, i) => (
					  <span key={i} className="d-inline-flex align-items-center me-3">
						<span style={{ fontSize: "1.4rem", marginRight: "5px" }}>{getFlagEmoji(cc)}</span>
						<span className="text-uppercase" style={{ color: "#bbb" }}>{cc}</span>
					  </span>
					))}
				  </div>
				)}
			  </div>

			  {/* 🖼️ Vendor Logo - centered middle-right */}
			  {cert.vendor_img && (
				<div
				  className="d-flex align-items-center justify-content-center"
				  style={{
					minWidth: "60px",
					height: "100%",
					marginRight: "35px"
				  }}
				>
				  <img
					src={cert.vendor_img}
					alt="Vendor Logo"
					className="rounded-circle"
					style={{
					  width: "55px",
					  height: "55px",
					  objectFit: "cover",
					  border: "2px solid #444",
					  background: "#1e1e1e",
					}}
				  />
				</div>
			  )}
			</div>
            {/* 📃 Description */}
			<div
			  className="mt-4 p-4 mb-3 position-relative"
			  style={{
				borderRadius: "12px",
				border: "1px solid #2d2d2d",
				background: "linear-gradient(45deg, #3b3b3b, #2d2d2d)",
				boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
				textAlign: "left",
				paddingBottom: "30px", // 🔧 no logo, less padding
			  }}
			>
			  {/* 🔎 External search buttons + resolved badge */}
			  {cve && (
				<div
				  style={{
					position: "absolute",
					top: "10px",
					right: "15px",
					display: "flex",
					gap: "10px",
				  }}
				>
				  <a
					href={`https://www.google.com/search?q=%22${encodeURIComponent(cve)}%22`}
					target="_blank"
					rel="noopener noreferrer"
					className="btn btn-sm btn-outline-light"
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					title="Search on Google"
				  >
					<i className="bi bi-google" />
				  </a>
				  <a
					href={`https://twitter.com/search?q=${encodeURIComponent(cve)}`}
					target="_blank"
					rel="noopener noreferrer"
					className="btn btn-sm btn-outline-light"
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					title="Search on X (Twitter)"
				  >
					<i className="bi bi-twitter-x" />
				  </a>
				  <a
					href={`https://github.com/search?q=%22${encodeURIComponent(cve)}%22`}
					target="_blank"
					rel="noopener noreferrer"
					className="btn btn-sm btn-outline-light"
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					title="Search on GitHub"
				  >
					<i className="bi bi-github" />
				  </a>

					{cert?.resolved_for?.toLowerCase() === country.toLowerCase() && (
					  <span className="resolved-label">
						<i
						  className="bi bi-patch-check-fill me-1"
						  style={{ color: "#4caf50", marginLeft: "10px" }}
						/>{" "}
						Resolved
					  </span>
					)}
					
					{cert?.resolved_for?.toLowerCase() !== country.toLowerCase() && (
					  <button
						onClick={() => handleResolve(cert.id)}
						className="btn btn-sm"
						style={{
						  backgroundColor: "#925a2a",
						  color: "#f8f9fa",
						  border: "1px solid #925a2a",
						  fontWeight: "bold",
						}}
					  >
						<i className="bi bi-check-circle me-1" />
						Mark as Resolved
					  </button>
					)}
				</div>
			  )}

			  {/* Title + Description */}
			  <h5
				style={{
				  marginBottom: "10px",
				  fontWeight: "bold",
				  color: "#ddd",
				  marginTop: "10px",
				}}
			  >
				Description
			  </h5>
			  <p
				style={{ fontSize: "14px", lineHeight: "1.6", color: "#bbb" }}
			  >
				{description || "No description available."}
			  </p>
			</div>

            {/* 📊 CVSS Breakdown */}
            <div className="d-flex flex-wrap gap-3">
			  <div
				className="p-3 flex-fill rounded text-center d-flex flex-column justify-content-center"
				style={{
				  minWidth: "260px",
				  backgroundColor: "#1e1e1e",
				  border: "1px solid #313131",
				  minHeight: "260px", // hauteur uniforme
				}}
			  >
				<p><strong>Attack Vector:</strong> <span className="text-danger">{attack_vector || "—"}</span></p>
				<p><strong>Attack Complexity:</strong> <span className="text-warning">{attack_complexity || "—"}</span></p>
				<p><strong>Privileges Required:</strong> <span className="text-warning">{privileges_required || "—"}</span></p>
				<p><strong>User Interaction:</strong> <span className="text-success">{user_interaction || "—"}</span></p>
				<p><strong>Scope:</strong> <span className="text-success">{scope || "—"}</span></p>
			  </div>

			  <div
				className="p-3 flex-fill rounded text-center d-flex flex-column justify-content-center"
				style={{
				  minWidth: "260px",
				  backgroundColor: "#1e1e1e",
				  border: "1px solid #313131",
				  minHeight: "260px",
				}}
			  >
				<p><strong>Confidentiality:</strong> <span className="text-danger">{confidentiality_impact || "—"}</span></p>
				<p><strong>Integrity:</strong> <span className="text-danger">{integrity_impact || "—"}</span></p>
				<p><strong>Availability:</strong> <span className="text-danger">{availability_impact || "—"}</span></p>
			  </div>

			  <div
				className="p-3 flex-fill rounded text-center d-flex flex-column justify-content-center"
				style={{
				  minWidth: "260px",
				  backgroundColor: "#1e1e1e",
				  border: "1px solid #313131",
				  minHeight: "260px",
				}}
			  >
				<p><strong>Severity:</strong> <span className="text-danger">{severity || "—"}</span></p>
				<p><strong>Exploitability Score:</strong> <span className="text-success">{exploitability_score || "—"}</span></p>
				<p><strong>Impact Score:</strong> <span className="text-warning">{impact_score || "—"}</span></p>
				<p><strong>CVSS:</strong> <span className="text-danger">{score_cvss || "—"}</span></p>
				<p><strong>CVSS Version:</strong> <span className="text-white">{cvss_version || "—"}</span></p>
			  </div>
			</div>

            {/* 🔗 References */}
            {Array.isArray(cve_refs) && cve_refs.length > 0 && (
              <div className="mt-3 mb-3 collapse-container">
                <button
                  className="collapse-toggle"
                  type="button"
                  onClick={() => setIsRefOpen(!isRefOpen)}
                >
                  <span><i className="bi bi-book" /> References</span>
                  <i className={`bi ${isRefOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                </button>
                <div className={`collapse-content ${isRefOpen ? "show" : ""} mt-2`}>
                  <ul className="list-group list-group-flush">
                    {cve_refs.map((url, i) => (
                      <li key={i} className="list-group-item collapse-list-item">
                        <i className="bi bi-link-45deg" />{" "}
                        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* 🔢 Affected Versions */}
            {Array.isArray(affected_versions) && affected_versions.length > 0 && (
              <div className="mt-3 mb-3 collapse-container">
                <button
                  className="collapse-toggle"
                  type="button"
                  onClick={() => setIsAffectedOpen(!isAffectedOpen)}
                >
                  <span><i className="bi bi-exclamation-circle" /> Affected Versions</span>
                  <i className={`bi ${isAffectedOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                </button>
                <div className={`collapse-content ${isAffectedOpen ? "show" : ""} mt-2`}>
                  <ul className="list-group list-group-flush">
                    {affected_versions.map((entry, i) => (
                      <li key={i} className="list-group-item collapse-list-item">
                        <i className="bi bi-box" /> <strong>{entry.product || "—"}</strong>{" "}
                        <span style={{ color: "#b3947a" }}><b>{entry.version || "N/A"}</b></span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* 🧪 PoCs */}
            {Array.isArray(poc_refs) && poc_refs.length > 0 && (
              <div className="mt-3 mb-3 collapse-container">
                <button
                  className="collapse-toggle"
                  type="button"
                  onClick={() => setIsPocOpen(!isPocOpen)}
                >
                  <span><i className="bi bi-terminal" /> Exploit Proof of Concept (PoC)</span>
                  <i className={`bi ${isPocOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
                </button>
                <div className={`collapse-content ${isPocOpen ? "show" : ""} mt-2`}>
                  <ul className="list-group list-group-flush">
                    {poc_refs.map((url, i) => (
                      <li key={i} className="list-group-item collapse-list-item">
                        <i className="bi bi-link-45deg" />{" "}
                        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* 🛰️ Cockpit ID */}
            {guid && (
              <div className="mt-3 text-light">
                <strong>Cockpit ID:</strong>{" "}
                <span className="text-secondary">{guid}</span> - {created_at}
              </div>
            )}
          </div>
        </SmoothCollapse>
      </td>
    </tr>
  );
};

export default CertRowDetails;
